import { Guid } from './types'

export type CanDoResultDto = {
    id: Guid
    score: number | null
}

export type ClassResultPayload = {
    studentId: Guid
    resultType: number | null
    comment: string
    canDoResults: CanDoResultDto[]
    isLevelChangeRecommended?: boolean | null
    levelChangeComment?: string | null
    isAdvisingSessionRecommended?: boolean | null
    advisingSessionComment?: string | null
}

export type StudentResultInput = {
    userId: Guid
    comment: string
    contentItemResultType?: number | undefined
    scores: Map<Guid, number>
    recommendLevelChange?: boolean
    recommendAdvSession?: boolean
    levelChangeRecommendation?: string
    advSessionRecommendation?: string
}

const toNullableComment = (comment?: string): string | null => {
    const trimmedComment = comment?.trim()
    return trimmedComment || null
}

const getRecommendationFields = (student: StudentResultInput): Pick<ClassResultPayload,
'isLevelChangeRecommended' | 'levelChangeComment' | 'isAdvisingSessionRecommended' | 'advisingSessionComment'> => ({
    isLevelChangeRecommended: student.recommendLevelChange ?? false,
    levelChangeComment: toNullableComment(student.levelChangeRecommendation),
    isAdvisingSessionRecommended: student.recommendAdvSession ?? false,
    advisingSessionComment: toNullableComment(student.advSessionRecommendation)
})

/**
 * Adapts a student's in-memory assessment state into the DTO expected by
 * /bookapi/api/class/:classId/result.
 *
 * @param student - The student state to transform.
 * @param isFinal - When true, the student's contentItemResultType is sent as
 *                  resultType; otherwise resultType is null for partial saves.
 * @param includeRecommendations - When true, recommendation fields are included in the payload.
 */
export const adaptStudentResult = (
    student: StudentResultInput,
    isFinal: boolean = false,
    includeRecommendations: boolean = false
): ClassResultPayload => ({
    studentId: student.userId,
    resultType: isFinal ? (student.contentItemResultType ?? null) : null,
    comment: student.comment ?? '',
    canDoResults: Array.from(student.scores.entries()).map(([id, score]) => ({ id, score: score === 0 ? null : score })),
    ...(includeRecommendations ? getRecommendationFields(student) : {})
})
